<template>
  <aside class="sidebar" :class="{ collapsed, 'mobile-open': mobileOpen }">
    <div class="sidebar-top">
      <button class="collapse-btn" :title="t('nav.collapse')" @click="toggleCollapse">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ flip: collapsed }"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <div class="sidebar-logo">
        <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
          <rect width="36" height="36" rx="8" fill="var(--sb-active)"/>
          <path d="M10 14h16M10 18h10M10 22h13" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/>
        </svg>
        <span class="label">DevTask</span>
      </div>
    </div>

    <div class="sidebar-user">
      <div class="user-avatar">{{ initials }}</div>
      <div class="user-info">
        <div class="user-name">{{ user?.name }}</div>
        <div class="user-role">{{ roleLabel }}</div>
      </div>
    </div>

    <nav class="sidebar-nav">
      <router-link to="/" exact-active-class="active" class="nav-item" :title="t('nav.dashboard')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
        <span class="label">{{ t('nav.dashboard') }}</span>
      </router-link>

      <router-link
        :to="isExternal ? '/tasks' : '/tasks?mine=1'"
        class="nav-item"
        :title="t('nav.myTasks')"
        :class="{ active: $route.path === '/tasks' && (isExternal || $route.query.mine) }"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
        <span class="label">{{ t('nav.myTasks') }}</span>
      </router-link>

      <router-link v-if="!isExternal" to="/tasks" class="nav-item" :title="t('nav.teamTasks')" :class="{ active: $route.path === '/tasks' && !$route.query.mine }">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="label">{{ t('nav.teamTasks') }}</span>
      </router-link>

      <router-link to="/projects" active-class="active" class="nav-item" :title="t('nav.projects')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
        <span class="label">{{ t('nav.projects') }}</span>
      </router-link>

      <router-link v-if="!isExternal" to="/team" active-class="active" class="nav-item" :title="t('nav.team')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="label">{{ t('nav.team') }}</span>
      </router-link>

      <router-link v-if="!isExternal" to="/archive" active-class="active" class="nav-item" :title="t('nav.archive')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
        <span class="label">{{ t('nav.archive') }}</span>
      </router-link>

      <router-link v-if="!isExternal" to="/clients" active-class="active" class="nav-item" :title="t('nav.clients')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/></svg>
        <span class="label">{{ t('nav.clients') }}</span>
      </router-link>
    </nav>

    <div class="sidebar-bottom">
      <button class="nav-item" :title="t('nav.language')" @click="toggleLocale">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
        <span class="label">{{ t('nav.language') }}</span>
      </button>
      <button class="nav-item" :title="t('nav.theme')" @click="toggleTheme">
        <svg v-if="isDark" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
        <span class="label">{{ t('nav.theme') }}</span>
      </button>
      <button class="nav-item logout-btn" :title="t('nav.signOut')" @click="$emit('logout')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        <span class="label">{{ t('nav.signOut') }}</span>
      </button>
    </div>
  </aside>
</template>

<script>
import { computed, ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useI18n } from '@/composables/useI18n'
import { useTheme } from '@/composables/useTheme'

export default {
  name: 'AppSidebar',
  props: { mobileOpen: { type: Boolean, default: false } },
  emits: ['logout'],
  setup() {
    const { currentUser, isManager: _isManager, isExternalClient } = useAuth()
    const { t, toggleLocale } = useI18n()
    const { toggleTheme, isDark } = useTheme()

    const user      = computed(() => currentUser())
    const isExternal = computed(() => isExternalClient())
    const isManager  = computed(() => _isManager())
    const roleLabel  = computed(() => {
      if (isExternal.value) return t('roles.client')
      return user.value?.role === 'manager' ? t('roles.manager') : t('roles.developer')
    })
    const initials = computed(() => {
      const name = user.value?.name || ''
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'
    })

    const collapsed = ref(localStorage.getItem('todo_sidebar_collapsed') === '1')
    function toggleCollapse() {
      collapsed.value = !collapsed.value
      localStorage.setItem('todo_sidebar_collapsed', collapsed.value ? '1' : '0')
    }

    return { user, isExternal, isManager, roleLabel, initials, t, toggleLocale, toggleTheme, isDark, collapsed, toggleCollapse }
  },
}
</script>

<style scoped>
/* Deep glassy floating panel — dark in both themes (design system signature). */
.sidebar {
  width: 248px;
  flex-shrink: 0;
  margin: 16px;
  min-height: calc(100vh - 32px);
  border-radius: 24px;
  background: linear-gradient(160deg, var(--sb-bg), var(--sb-bg-2));
  color: var(--sb-ink);
  display: flex;
  flex-direction: column;
  padding: 22px 14px;
  box-shadow: 0 8px 24px rgba(16,22,40,.16);
  position: sticky;
  top: 16px;
}
.sidebar-top {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 8px;
  margin-bottom: 20px;
  direction: ltr; /* keep the collapse button pinned to the physical-left in both languages */
}
.sidebar-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 17px;
  font-weight: 800;
  color: var(--sb-ink);
  min-width: 0;
}
.collapse-btn {
  border: none; background: none; cursor: pointer;
  color: var(--sb-ink-2);
  display: flex; padding: 4px; border-radius: 8px;
  flex-shrink: 0;
}
.collapse-btn:hover { color: var(--sb-ink); background: var(--sb-hover); }
/* Sidebar is always on the left; chevron points left to collapse, flips to expand. */
.collapse-btn svg.flip { transform: rotate(180deg); }
.sidebar-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px 16px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--sb-divider);
}
.user-avatar {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: var(--sb-active);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.user-name { font-size: 13px; font-weight: 600; line-height: 1.2; color: var(--sb-ink); }
.user-role { font-size: 11px; color: var(--sb-ink-2); margin-top: 1px; }

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0;
  flex: 1;
}
.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 12px;
  border-radius: 12px;
  color: var(--sb-ink-2);
  font-size: 13.5px;
  font-weight: 500;
  text-decoration: none;
  transition: background .15s, color .15s;
  cursor: pointer;
  border: none;
  background: none;
  width: 100%;
  text-align: start;
}
.nav-item svg { flex-shrink: 0; color: var(--sb-icon); }
.nav-item:hover  { background: var(--sb-hover); color: var(--sb-ink); }
.nav-item.active { background: var(--sb-hover); color: #fff; font-weight: 600; }
.nav-item.active svg { color: var(--sb-active); }

.sidebar-bottom {
  padding: 12px 0 0;
  border-top: 1px solid var(--sb-divider);
  margin-top: 8px;
}
.logout-btn { color: var(--sb-ink-2); }
.logout-btn:hover { color: #fff; background: var(--sb-hover); }

.label { white-space: nowrap; min-width: 0; overflow: hidden; }

/* ── Collapsed (icon rail) ── */
.sidebar { transition: width .18s ease; }
.sidebar.collapsed { width: 76px; padding: 22px 12px; }
.sidebar.collapsed .label { display: none; }
.sidebar.collapsed .sidebar-logo { display: none; }
.sidebar.collapsed .user-info { display: none; }
.sidebar.collapsed .sidebar-user { justify-content: center; padding: 0 0 16px; }
.sidebar.collapsed .nav-item { justify-content: center; padding: 10px 0; }

/* ── Mobile: off-canvas left drawer (< 768px) ── */
@media (max-width: 768px) {
  .sidebar {
    position: fixed; top: 0; bottom: 0; left: 0;
    margin: 0; border-radius: 0; height: 100vh; min-height: 100vh;
    width: 272px;
    transform: translateX(-110%);
    transition: transform .22s ease;
    z-index: 50;
  }
  .sidebar.mobile-open { transform: translateX(0); }
  /* always show the full sidebar in the drawer, even if desktop-collapsed */
  .sidebar.collapsed { width: 272px; padding: 22px 14px; }
  .sidebar.collapsed .label,
  .sidebar.collapsed .sidebar-logo,
  .sidebar.collapsed .user-info { display: revert; }
  .sidebar.collapsed .sidebar-user { justify-content: flex-start; }
  .sidebar.collapsed .nav-item { justify-content: flex-start; padding: 10px 12px; }
  .collapse-btn { display: none; }
}
</style>
